import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete,
  SelectChangeEvent,
} from '@mui/material';
import { useIssues } from '../context/IssueContext';
import { useUsers } from '../hooks/useUsers';
import { PictureAsPdf, Add as AddIcon } from '@mui/icons-material';
import { ArticleStatus, PdfFile, Article } from '../services/articleService';
import { ArticleFormData } from '../types/article';
import api from '../services/api';
import { User, UserRole, UserStatus, getUserFullName } from '../types/user';
import { Author } from '../types/author';
import { ReviewComment } from '../types/review';
import { toast } from 'react-hot-toast';
import UserForm from './UserForm';
import { useArticles } from '../context/ArticleContext';
import KeywordsField from './KeywordsField';

interface PdfCheckResponse {
  exists: boolean;
}

interface ArticleFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  initialData?: ArticleFormData;
  mode: 'add' | 'edit';
}

const initialFormData: ArticleFormData = {
  title: '',
  authors: [],
  abstract: '',
  keywords: [],
  doi: '',
  edn: '',
  issueId: 0,
  pages: '',
  status: ArticleStatus.DRAFT,
  submittedDate: new Date().toISOString().split('T')[0],
  pdfFile: undefined,
};

const ArticleForm: React.FC<ArticleFormProps> = ({ open, onClose, onSubmit, initialData, mode }) => {
  const [formData, setFormData] = useState<ArticleFormData>(initialData || initialFormData);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const { issues, loading: issuesLoading, error: issuesError, fetchIssues } = useIssues();
  const { users, loading: usersLoading, error: usersError, fetchUsers, getUsersByRole, createUser } = useUsers();
  const [authors, setAuthors] = useState<User[]>([]);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState<User | null>(null);
  const { deletePdfFile, fetchAllArticles } = useArticles();

  useEffect(() => {
    fetchIssues();
    loadAuthors();
  }, []);

  useEffect(() => {
    if (initialData) {
      console.log('Initial data in ArticleForm:', initialData);
      console.log('Тип pdfFile:', typeof initialData.pdfFile);
      console.log('Значение pdfFile:', initialData.pdfFile);

      // Парсим pdfFile, если он пришел как строка
      let pdfFileData: PdfFile | undefined;
      if (initialData.pdfFile) {
        console.log('Получены данные о PDF файле:', initialData.pdfFile);
        if (typeof initialData.pdfFile === 'string') {
          try {
            pdfFileData = JSON.parse(initialData.pdfFile);
            console.log('Распарсенный pdfFile:', pdfFileData);
          } catch (e) {
            console.error('Error parsing pdfFile JSON:', e);
            // Если не удалось распарсить JSON, возможно это просто путь к файлу
            pdfFileData = {
              file: initialData.pdfFile,
              path: `/uploads/${initialData.pdfFile}`
            };
            console.log('Создан pdfFileData из строки:', pdfFileData);
          }
        } else if (typeof initialData.pdfFile === 'object') {
          pdfFileData = initialData.pdfFile;
          console.log('Полученный pdfFile объект:', pdfFileData);
        }
      }

      // Обрабатываем keywords
      let keywordsData: string[] = [];
      if (initialData.keywords) {
        if (typeof initialData.keywords === 'string') {
          try {
            keywordsData = JSON.parse(initialData.keywords);
          } catch (e) {
            console.error('Error parsing keywords JSON:', e);
            keywordsData = [];
          }
        } else if (Array.isArray(initialData.keywords)) {
          keywordsData = initialData.keywords;
        }
      }

      // Обрабатываем авторов
      let authorsData: string[] = [];
      if (initialData.authors) {
        console.log('Обработка авторов из initialData:', initialData.authors);
        if (typeof initialData.authors === 'string') {
          try {
            authorsData = JSON.parse(initialData.authors);
          } catch (e) {
            console.error('Error parsing authors JSON:', e);
            authorsData = [];
          }
        } else if (Array.isArray(initialData.authors)) {
          authorsData = initialData.authors.map(author => {
            if (typeof author === 'string') return author;
            if (typeof author === 'object' && author && 'id' in author) {
              const authorObj = author as { id: string | number };
              return String(authorObj.id);
            }
            return String(author);
          });
        }
      }
      console.log('Обработанные ID авторов:', authorsData);

      // Проверяем существование файла
      const checkPdfFile = async () => {
        if (pdfFileData?.file) {
          console.log('Проверяем существование файла:', pdfFileData.file);

          try {
            // Проверяем существование файла через API
            const response = await api.get<PdfCheckResponse>(`/articles/${initialData.id}/pdf/check`);
            console.log('Ответ от сервера при проверке PDF:', response.data);

            if (response.data.exists) {
              const updatedFormData = {
                ...initialData,
                keywords: keywordsData,
                authors: authorsData,
                pdfFile: {
                  file: pdfFileData.file,
                  path: pdfFileData.path || `/uploads/${pdfFileData.file}`
                }
              };
              console.log('Устанавливаем данные формы с PDF:', updatedFormData);
              setFormData(updatedFormData);
            } else {
              console.log('PDF файл не найден на сервере');
              // Удаляем ссылку на файл через API
              try {
                await api.delete(`/articles/${initialData.id}/pdf`);
                console.log('Ссылка на PDF файл успешно удалена');
              } catch (deleteError) {
                console.error('Ошибка при удалении ссылки на PDF файл:', deleteError);
                toast.error('Ошибка при удалении ссылки на PDF файл');
              }

              setFormData({
                ...initialData,
                keywords: keywordsData,
                authors: authorsData,
                pdfFile: undefined
              });
            }
          } catch (error: any) {
            console.error('Ошибка при проверке PDF файла:', error);

            // Определяем тип ошибки и показываем соответствующее сообщение
            let errorMessage = 'Ошибка при проверке PDF файла';
            if (error.response) {
              // Ошибка от сервера
              errorMessage = error.response.data.message || errorMessage;
            } else if (error.request) {
              // Ошибка сети
              errorMessage = 'Сервер недоступен. Пожалуйста, проверьте подключение к интернету';
            }

            toast.error(errorMessage);

            // В случае ошибки оставляем текущее состояние
            setFormData({
              ...initialData,
              keywords: keywordsData,
              authors: authorsData,
              pdfFile: pdfFileData
            });
          }
        } else {
          console.log('Нет данных о PDF файле');
          setFormData({
            ...initialData,
            keywords: keywordsData,
            authors: authorsData,
            pdfFile: undefined
          });
        }
      };

      checkPdfFile();
    } else {
      setFormData(initialFormData);
    }
  }, [initialData]);

  useEffect(() => {
    console.log('Текущее состояние pdfFile в форме:', formData.pdfFile);
  }, [formData.pdfFile]);

  const loadAuthors = async () => {
    try {
      console.log('Начало загрузки авторов...');
      const authorList = await getUsersByRole(UserRole.AUTHOR);
      console.log('Полученный список авторов:', authorList);

      const mappedAuthors = authorList.map(user => ({
        ...user,
        role: user.role as UserRole,
        status: user.status as UserStatus
      }));
      console.log('Преобразованный список авторов:', mappedAuthors);

      setAuthors(mappedAuthors);
    } catch (error) {
      console.error('Ошибка при загрузке авторов:', error);
      toast.error('Ошибка при загрузке списка авторов');
    }
  };

  const handleAuthorChange = (event: any, newValue: User | null) => {
    setSelectedAuthor(newValue);
  };

  const handleAddAuthor = () => {
    if (selectedAuthor) {
      console.log('Добавление автора:', selectedAuthor);

      // Проверяем, не добавлен ли уже этот автор
      if (formData.authors.includes(selectedAuthor.id)) {
        toast.error('Этот автор уже добавлен');
        return;
      }

      setFormData(prev => {
        const newAuthors = [...prev.authors, selectedAuthor.id];
        console.log('Новый список авторов:', newAuthors);
        return {
          ...prev,
          authors: newAuthors
        };
      });
      setSelectedAuthor(null);
    } else {
      setUserFormOpen(true);
    }
  };

  const handleRemoveAuthor = (authorId: string) => {
    console.log('Удаление автора:', authorId);
    setFormData(prev => {
      const newAuthors = prev.authors.filter(id => id !== authorId);
      console.log('Новый список авторов после удаления:', newAuthors);
      return {
        ...prev,
        authors: newAuthors
      };
    });
  };

  const handleCreateAuthor = async (userData: any) => {
    try {
      const newAuthor = await createUser({
        ...userData,
        role: 'author',
        status: 'active'
      });

      // Обновляем список авторов
      await loadAuthors();

      // Добавляем нового автора в форму
      setFormData(prev => ({
        ...prev,
        authors: [...prev.authors, newAuthor.id]
      }));

      setUserFormOpen(false);
      toast.success('Автор успешно добавлен');
    } catch (error) {
      console.error('Ошибка при создании автора:', error);
      toast.error('Ошибка при создании автора');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'issueId' ? Number(value) : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Проверяем тип файла
    if (file.type !== 'application/pdf') {
      toast.error('Только PDF файлы разрешены');
      e.target.value = ''; // Очищаем input
      return;
    }

    // Проверяем размер файла (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB в байтах
    if (file.size > maxSize) {
      toast.error('Размер файла не должен превышать 10MB');
      e.target.value = ''; // Очищаем input
      return;
    }

    // Создаем URL для предпросмотра
    const fileUrl = URL.createObjectURL(file);
    
    setSelectedPdfFile(file);

    setFormData(prev => ({
      ...prev,
      pdfFile: {
        file: file.name,
        path: fileUrl
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.issueId) return;

    try {
      // Создаем FormData
      const formDataToSubmit = new FormData();

      // Добавляем основные поля
      formDataToSubmit.append('title', formData.title);
      formDataToSubmit.append('abstract', formData.abstract);
      formDataToSubmit.append('keywords', JSON.stringify(formData.keywords));

      // Отправляем только ID авторов, гарантируя что они в виде строк и удаляем дубликаты
      const authorIds = formData.authors
        .map(author => {
          if (typeof author === 'string') return author;
          if (typeof author === 'object' && author && 'id' in author) {
            const authorObj = author as { id: string | number };
            return String(authorObj.id);
          }
          return String(author);
        })
        .filter((id, index, self) => self.indexOf(id) === index);

      console.log('Уникальные ID авторов:', authorIds);

      // Проверяем, что все ID авторов существуют
      const existingAuthors = authors.filter(author => authorIds.includes(String(author.id)));
      console.log('Найденные авторы в системе:', existingAuthors);

      if (existingAuthors.length !== authorIds.length) {
        console.error('Несоответствие количества авторов:', {
          expected: authorIds.length,
          found: existingAuthors.length,
          authorIds,
          existingAuthors: existingAuthors.map(a => a.id)
        });
        toast.error('Некоторые авторы не найдены в системе');
        return;
      }

      formDataToSubmit.append('authors', JSON.stringify(authorIds));
      formDataToSubmit.append('doi', formData.doi);
      formDataToSubmit.append('edn', formData.edn);
      formDataToSubmit.append('status', formData.status || ArticleStatus.DRAFT);
      formDataToSubmit.append('issueId', String(formData.issueId));
      formDataToSubmit.append('pages', formData.pages || '');

      // Добавляем PDF файл, если он есть
      if (selectedPdfFile) {
        console.log('Подготовка PDF файла для отправки:', {
          name: selectedPdfFile.name,
          size: selectedPdfFile.size,
          type: selectedPdfFile.type
        });
        formDataToSubmit.append('pdfFile', selectedPdfFile);
      } else if (formData.pdfFile?.file) {
        console.log('Новый файл не выбран, оставляем существующий PDF на сервере:', formData.pdfFile.file);
      }

      // Логируем данные перед отправкой
      console.log('Отправляемые данные формы:', {
        title: formData.title,
        abstract: formData.abstract,
        keywords: formData.keywords,
        authors: authorIds,
        doi: formData.doi,
        edn: formData.edn,
        status: formData.status,
        issueId: formData.issueId,
        pages: formData.pages,
        pdfFile: formData.pdfFile
      });

      // Отправляем данные
      await onSubmit(formDataToSubmit);

      // После успешного сохранения обновляем данные формы
      if (formData.id) {
        try {
          const response = await api.get(`/articles/${formData.id}`);
          const updatedArticle = response.data as Article;
          console.log('Получены обновленные данные статьи:', updatedArticle);

          // Проверяем и парсим pdfFile, если он пришел как строка
          let pdfFileData: PdfFile | undefined;
          if (updatedArticle.pdfFile) {
            if (typeof updatedArticle.pdfFile === 'string') {
              try {
                pdfFileData = JSON.parse(updatedArticle.pdfFile);
              } catch (e) {
                console.error('Error parsing pdfFile JSON:', e);
                // Если не удалось распарсить JSON, создаем объект из строки
                pdfFileData = {
                  file: updatedArticle.pdfFile,
                  path: `/uploads/${updatedArticle.pdfFile}`
                };
              }
            } else if (typeof updatedArticle.pdfFile === 'object') {
              pdfFileData = updatedArticle.pdfFile;
            }
          }
          console.log('Обработанные данные PDF файла:', pdfFileData);

          // Обновляем состояние формы с актуальными данными
          setFormData(prev => ({
            ...prev,
            ...updatedArticle,
            pdfFile: pdfFileData,
            authors: updatedArticle.authors?.map(author => author.id) || [],
            issueId: updatedArticle.issue?.id || 0
          }));

          // Обновляем список статей
          await fetchAllArticles();
        } catch (error) {
          console.error('Ошибка при обновлении данных формы:', error);
        }
      }
    } catch (error: any) {
      let errorMessage = 'Ошибка при создании статьи';

      if (error.response) {
        const serverError = error.response.data;

        if (typeof serverError === 'string') {
          errorMessage = serverError;
        } else if (serverError && typeof serverError === 'object') {
          errorMessage = serverError.message || 'Неизвестная ошибка сервера';
        }
      }

      toast.error(errorMessage);
    }
  };

  const handleKeywordsChange = (newKeywords: string[]) => {
    setFormData(prev => ({
      ...prev,
      keywords: newKeywords
    }));
  };

  const handleDeletePdf = async () => {
    if (!formData.id) {
      console.log('ID статьи не найден, невозможно удалить PDF файл');
      toast.error('ID статьи не найден');
      return;
    }

    // Запрашиваем подтверждение
    if (!window.confirm('Вы уверены, что хотите удалить PDF файл?')) {
      return;
    }

      try {
        console.log('Начинаем удаление PDF файла для статьи:', formData.id);
        console.log('Текущий PDF файл:', formData.pdfFile);

        // Очищаем URL объект, если он был создан
        if (formData.pdfFile?.path && formData.pdfFile.path.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(formData.pdfFile.path);
        } catch (error) {
          console.error('Ошибка при очистке URL объекта:', error);
        }
      }

      // Проверяем существование файла на сервере
      try {
        const checkResponse = await api.get<{ data: PdfCheckResponse }>(`/articles/${formData.id}/pdf/check`);
        if (!checkResponse.data.data.exists) {
          console.log('PDF файл не существует на сервере');
          // Обновляем состояние формы
          setFormData(prev => ({
            ...prev,
            pdfFile: undefined
          }));
          setSelectedPdfFile(null);
          return;
        }
      } catch (error) {
        console.error('Ошибка при проверке существования файла:', error);
        }

        // Вызываем API для удаления файла
        const response = await deletePdfFile(formData.id);
        console.log('Ответ от сервера при удалении PDF:', response);

      // Обновляем состояние формы
        setFormData(prev => ({
          ...prev,
            pdfFile: undefined
        }));
          setSelectedPdfFile(null);

        // Обновляем список статей
        await fetchAllArticles();

        toast.success('PDF файл успешно удален');
      } catch (error) {
        console.error('Ошибка при удалении PDF файла:', error);
        toast.error('Ошибка при удалении PDF файла');

        // В случае ошибки получаем актуальное состояние статьи
        try {
          console.log('Получаем актуальное состояние статьи после ошибки');
          const response = await api.get(`/articles/${formData.id}`);
          const updatedArticle = response.data as Article;
          console.log('Актуальное состояние статьи:', updatedArticle);

          let pdfFileData: PdfFile | undefined;
          if (updatedArticle.pdfFile) {
            if (typeof updatedArticle.pdfFile === 'string') {
              try {
                pdfFileData = JSON.parse(updatedArticle.pdfFile);
              } catch (e) {
                console.error('Error parsing pdfFile JSON:', e);
              }
            } else if (typeof updatedArticle.pdfFile === 'object') {
              pdfFileData = updatedArticle.pdfFile;
            }
          }

          setFormData(prev => ({
            ...prev,
            pdfFile: pdfFileData
          }));
        } catch (fetchError) {
          console.error('Ошибка при получении актуального состояния статьи:', fetchError);
        }
    }
  };

  // Добавляем очистку URL объектов при размонтировании компонента
  useEffect(() => {
    return () => {
      if (formData.pdfFile?.path && formData.pdfFile.path.startsWith('blob:')) {
        URL.revokeObjectURL(formData.pdfFile.path);
      }
    };
  }, [formData.pdfFile]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'add' ? 'Добавить статью' : 'Редактировать статью'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название статьи"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Авторы
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {formData.authors?.map((authorId) => {
                  const author = authors.find(a => a.id === authorId);
                  return author ? (
                    <Chip
                      key={authorId}
                      label={`${getUserFullName(author)} (${author.email})`}
                      onDelete={() => handleRemoveAuthor(authorId)}
                    />
                  ) : null;
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Autocomplete
                  fullWidth
                  options={authors.filter(author => !formData.authors.includes(author.id))}
                  getOptionLabel={(option) => `${getUserFullName(option)} (${option.email})`}
                  value={selectedAuthor}
                  onChange={handleAuthorChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Поиск автора"
                      placeholder="Начните вводить имя или email"
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddAuthor}
                >
                  {selectedAuthor ? 'Добавить' : 'Новый автор'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <KeywordsField
                keywords={formData.keywords || []}
                onChange={handleKeywordsChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Аннотация"
                name="abstract"
                value={formData.abstract}
                onChange={handleChange}
                required
                multiline
                rows={4}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="DOI"
                name="doi"
                value={formData.doi}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="EDN (eLIBRARY Document Number)"
                name="edn"
                value={formData.edn}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Выпуск</InputLabel>
                <Select
                  name="issueId"
                  value={formData.issueId.toString()}
                  onChange={handleChange}
                  label="Выпуск"
                >
                  {issues.map((issue) => (
                    <MenuItem key={issue.id} value={issue.id.toString()}>
                      {issue.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Страницы"
                name="pages"
                value={formData.pages}
                onChange={handleChange}
                required
                placeholder="Например: 123-145"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Статус"
                >
                  <MenuItem value={ArticleStatus.DRAFT}>Черновик</MenuItem>
                  <MenuItem value={ArticleStatus.SUBMITTED}>Отправлено</MenuItem>
                  <MenuItem value={ArticleStatus.UNDER_REVIEW}>На рецензировании</MenuItem>
                  <MenuItem value={ArticleStatus.REJECTED}>Отклонено</MenuItem>
                  <MenuItem value={ArticleStatus.ACCEPTED}>Принято</MenuItem>
                  <MenuItem value={ArticleStatus.PUBLISHED}>Опубликовано</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  PDF файл статьи
                </Typography>
                {formData.pdfFile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PictureAsPdf color="primary" />
                    <Typography variant="body2">
                      Текущий файл: {formData.pdfFile?.file || 'Файл не загружен'}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={handleDeletePdf}
                    >
                      Удалить
                    </Button>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PictureAsPdf />}
                  sx={{ mt: 1 }}
                >
                  {formData.pdfFile ? 'Заменить PDF' : 'Загрузить PDF'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained" color="primary">
            {mode === 'add' ? 'Добавить' : 'Сохранить'}
          </Button>
        </DialogActions>
      </form>

      <UserForm
        open={userFormOpen}
        onClose={() => setUserFormOpen(false)}
        onSubmit={handleCreateAuthor}
        mode="add"
      />
    </Dialog>
  );
};

export default ArticleForm;